import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Avatar,
  useTheme,
  SxProps,
  Theme,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PeopleIcon from '@mui/icons-material/People';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StarIcon from '@mui/icons-material/Star';

interface AdminHeaderProps {
  title: string;
  description: string;
  pageType: 'users' | 'statistics' | 'ai-management' | 'sponsors';
  sx?: SxProps<Theme>;
}

export const AdminHeader: React.FC<AdminHeaderProps> = ({
  title,
  description,
  pageType,
  sx,
}) => {
  const theme = useTheme();

  const getPageIcon = () => {
    switch (pageType) {
      case 'users':
        return <PeopleIcon sx={{ fontSize: '1.5rem' }} />;
      case 'statistics':
        return <AnalyticsIcon sx={{ fontSize: '1.5rem' }} />;
      case 'ai-management':
        return <SmartToyIcon sx={{ fontSize: '1.5rem' }} />;
      case 'sponsors':
        return <StarIcon sx={{ fontSize: '1.5rem' }} />;
      default:
        return <AdminPanelSettingsIcon sx={{ fontSize: '1.5rem' }} />;
    }
  };

  const getPageLabel = () => {
    switch (pageType) {
      case 'users':
        return 'ユーザー管理';
      case 'statistics':
        return '分析・統計';
      case 'ai-management':
        return 'エキスパート管理';
      case 'sponsors':
        return 'スポンサー管理';
      default:
        return '管理者機能';
    }
  };

  return (
    <Paper
      elevation={1}
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderRadius: 1,
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        ...sx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1.5, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          textAlign: { xs: 'center', sm: 'left' },
        }}
      >
        <Avatar
          sx={{
            width: { xs: 40, sm: 48 },
            height: { xs: 40, sm: 48 },
            fontSize: { xs: '1rem', sm: '1.25rem' },
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          }}
        >
          {getPageIcon()}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 0.5,
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: { xs: 'center', sm: 'flex-start' },
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                textAlign: { xs: 'center', sm: 'left' },
              }}
            >
              {title}
            </Typography>
            <Chip
              label={getPageLabel()}
              size="small"
              color="primary"
              variant="outlined"
              sx={{
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                height: { xs: 20, sm: 24 },
              }}
            />
          </Box>

          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              lineHeight: 1.4,
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              textAlign: { xs: 'center', sm: 'left' },
            }}
          >
            {description}
          </Typography>
        </Box>
      </Box>

      {/* 管理者権限の表示 */}
      <Box
        sx={{
          mt: { xs: 1, sm: 1.5 },
          pt: { xs: 1, sm: 1.5 },
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{
            display: 'block',
            textAlign: 'center',
            fontStyle: 'italic',
            fontSize: { xs: '0.7rem', sm: '0.75rem' },
          }}
        >
          🔧 管理者権限でアクセスしています
        </Typography>
      </Box>
    </Paper>
  );
};
